import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { DATE_FORMAT_NUMERIC_WITH_TIMESTAMP } from '../../utils/dateUtils';
import {
  EXCLUDED_ACTORS_TABLE_KEY,
  EXPORT_GUIDE,
  EXPORT_STEPS,
  INCLUDED_ACTORS_TABLE_KEY,
  STATUSES,
} from '../constants/export.constants';

export const getExportDataInitState = ({ changedItemsList = [] }) => {
  return {
    files: [],
    selectedWorkflows: [],
    selectedDate: null,
    selectedChangedItems: changedItemsList.map((item) => item.Value),
    [INCLUDED_ACTORS_TABLE_KEY]: [],
    [EXCLUDED_ACTORS_TABLE_KEY]: [],
  };
};

export const getExportPreviewPayload = ({ exportData }) => {
  const {
    files,
    selectedWorkflows = [],
    selectedDate = null,
    selectedChangedItems = [],
    includedActors = [],
    excludedActors = [],
  } = exportData;

  return {
    file: files[0],
    request: {
      selectedWorkflows,
      selectedDate: selectedDate || null,
      selectedChangedItems,
      selectedUser: includedActors[0]?.code || null,
      usersToExclude: excludedActors.map((actor) => actor.code),
    },
  };
};

export const getExportPreviewPackagesPayload = ({ selectedPackages }) => {
  return {
    request: {
      SelectedPackageIds: selectedPackages.map((build) => build.PK_ID),
    },
  };
};

export const getExportPayload = ({
  rows = [],
  selectedRows = new Set(),
  targetDirectoryName,
  buildFileName = '',
  buildFileComment = '',
  buildFileUser = '',
}) => {
  const componentsToBeExported = rows.filter((row) => selectedRows.has(row.id));
  return {
    componentsToBeExported,
    targetDirectoryName,
    BuildFileUser: buildFileUser,
    BuildFileName: buildFileName,
    BuildFileComment: buildFileComment,
  };
};

export const modifyRows = (rows = []) => {
  return rows.map((row) => ({ ...row, id: uuidv4() }));
};

export const getSaveBuildFilePayload = ({
  rows = [],
  selectedRows = new Set(),
  buildFileName = '',
  buildFileComment = '',
  buildFileUser = '',
}) => {
  const componentsToBeSaved = rows.filter((row) => selectedRows.has(row.id));

  return {
    ComponentsToBeSaved: componentsToBeSaved,
    BuildFileUser: buildFileUser,
    BuildFileName: buildFileName,
    BuildFileComment: buildFileComment,
  };
};

export const getModifiedActorsRows = ({ rowId, previousRows = [], newRows = [] }) => {
  const rowIndex = previousRows.findIndex((row) => row.rowId === rowId);

  const modifiedNewRows = newRows.map((row) => ({
    rowId: uuidv4(),
    id: row.ACT_ID,
    code: row.RSR_CODE,
    name: `${row.ACT_FIRST_NAME} ${row.ACT_LAST_NAME}`,
  }));

  if (rowIndex === -1) {
    return modifiedNewRows;
  }

  return [
    ...previousRows.slice(0, rowIndex),
    ...modifiedNewRows,
    ...previousRows.slice(rowIndex + 1),
  ];
};

export const updateStatusesWithCancelResponse = (exportStatuses, currentExportStatuses) => {
  const exportStatusesNames = exportStatuses.map((s) => s.Name);

  return currentExportStatuses.map((exportStatus) => {
    const statusFromResponse = exportStatuses.find((s) => s.Name === exportStatus.Name);
    return exportStatusesNames.includes(exportStatus.Name)
      ? {
          ...exportStatus,
          Status: statusFromResponse.StatusCode,
          Messages: statusFromResponse.Messages,
        }
      : {
          ...exportStatus,
          Status: [STATUSES.COMPLETE, STATUSES.ERROR].includes(exportStatus.Status)
            ? exportStatus.Status
            : STATUSES.CANCEL,
        };
  });
};

export const updateExportStatuses = (proccesedItemsStatuses, resultRows) => {
  return resultRows.map((row) => {
    const statusFromResponse = proccesedItemsStatuses.find((item) => item.Name === row.Name);
    return statusFromResponse
      ? {
          ...row,
          Status: statusFromResponse.StatusCode,
          Messages: statusFromResponse.Messages,
        }
      : { ...row };
  });
};

export const modifyDate = (date) => {
  if (!moment(date).isValid()) {
    return;
  }

  return moment(date).format(DATE_FORMAT_NUMERIC_WITH_TIMESTAMP);
};

export const getInfoTextByStep = (step, isPackagesExport) => {
  switch (step) {
    case EXPORT_STEPS.INITIAL_STEP:
      return isPackagesExport
        ? EXPORT_GUIDE.startExportStepPackagesDescription
        : EXPORT_GUIDE.startExportStepDescription;
    case EXPORT_STEPS.START_EXPORT:
      return EXPORT_GUIDE.previewStepInfo;
    case EXPORT_STEPS.PREVIEW:
      return EXPORT_GUIDE.cancelInfo;
    case EXPORT_STEPS.EXPORT_COMPLETED:
      return EXPORT_GUIDE.completeStepInfo;
    default:
      return '';
  }
};

export const getExportCountByStatusCode = (rows, statusCode) => {
  return rows?.filter((record) => record?.Status === statusCode).length;
};

export const getRecordTitle = (errorCount) => {
  return errorCount === 1 ? 'record' : 'records';
};
